import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Boxes, Pencil, Trash2, Layers, Box } from "lucide-react";
import api from "@/lib/api";
import { RESOURCE_TYPES, TYPE_META, formatEuro } from "@/lib/resources";
import LoadingScreen from "@/components/LoadingScreen";
import ResourceFormModal from "@/components/ResourceFormModal";
import { Button } from "@/components/ui/button";
import ListSearch from "@/components/ListSearch";
import Highlight from "@/components/Highlight";
import { matchesQuery, buildHaystack } from "@/lib/search";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || {};
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`font-normal gap-1.5 ${meta.className || ""}`}>
      {Icon && <Icon className="h-3 w-3" />} {type}
    </Badge>
  );
}

export default function Resources() {
  const [resources, setResources] = useState(null);
  const [units, setUnits] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [typeFilter, setTypeFilter] = useState("todos");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/resources"),
      api.get("/resource-units"),
      api.get("/contacts"),
      api.get("/model/activities"),
    ]).then(([r, u, c, a]) => { setResources(r.data); setUnits(u.data); setContacts(c.data); setActivities(a.data); })
      .catch(() => toast.error("No se pudieron cargar los recursos."));
  }, []);

  const filtered = useMemo(() => {
    if (!resources) return [];
    return resources.filter((r) =>
      (typeFilter === "todos" || r.type === typeFilter) &&
      matchesQuery(search, buildHaystack(r))
    );
  }, [resources, search, typeFilter]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); setModalOpen(true); };

  const onSaved = (saved, wasEditing) => {
    setResources((list) => {
      const next = wasEditing
        ? list.map((r) => (r.resource_id === saved.resource_id ? saved : r))
        : [...list, saved];
      return [...next].sort((a, b) => a.name.localeCompare(b.name, "es"));
    });
    if (saved.unit && !units.includes(saved.unit)) setUnits((u) => [...u, saved.unit]);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/resources/${toDelete.resource_id}`);
      setResources((list) => list.filter((r) => r.resource_id !== toDelete.resource_id));
      toast.success("Recurso eliminado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    } finally {
      setToDelete(null);
    }
  };

  if (!resources) return <LoadingScreen text="Cargando recursos…" />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="resources-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="caption mb-2">Catálogo global</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Gestión de recursos</h1>
          <p className="text-muted-foreground mt-2">Define tus recursos una vez y reutilízalos en todos tus proyectos.</p>
        </div>
        <Button className="rounded-full gap-2 h-11 px-5" onClick={openNew} data-testid="new-resource-button">
          <Plus className="h-4 w-4" /> Nuevo recurso
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <ListSearch className="flex-1" value={search} onChange={setSearch}
          placeholder="Buscar por cualquier contenido…" testId="resource-search-input" />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="resource-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {RESOURCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {resources.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/60 p-14 text-center" data-testid="resources-empty-state">
          <div className="mx-auto h-12 w-12 rounded-xl bg-accent text-olive flex items-center justify-center mb-4">
            <Boxes className="h-5 w-5" />
          </div>
          <h2 className="text-xl font-semibold">Aún no hay recursos</h2>
          <p className="text-sm text-muted-foreground mt-2 mb-5">Añade tu primer recurso al catálogo para empezar.</p>
          <Button className="rounded-full gap-2" onClick={openNew} data-testid="empty-new-resource-button">
            <Plus className="h-4 w-4" /> Nuevo recurso
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Clase</TableHead>
                  <TableHead className="text-right">Coste compra ud. (c/IVA)</TableHead>
                  <TableHead className="text-right">Precio venta ud.</TableHead>
                  <TableHead>Unidad</TableHead>
                  <TableHead className="hidden md:table-cell">Proveedor</TableHead>
                  <TableHead className="text-right w-[1%]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r, i) => (
                  <TableRow key={r.resource_id} data-testid={`resource-row-${i}`}>
                    <TableCell className="font-medium">
                      <Highlight text={r.name} query={search} />
                      {r.description && <div className="text-xs text-muted-foreground font-normal mt-0.5 max-w-xs truncate">{r.description}</div>}
                      {r.reference && <div className="text-xs text-muted-foreground font-mono">{r.reference}</div>}
                    </TableCell>
                    <TableCell><TypeBadge type={r.type} /></TableCell>
                    <TableCell data-testid={`resource-kind-${i}`}>
                      <Badge variant="outline" className={`font-normal gap-1.5 ${r.kind === "compuesto" ? "bg-[#C75B28]/10 text-[#C75B28] border-[#C75B28]/25" : "text-muted-foreground"}`}>
                        {r.kind === "compuesto" ? <Layers className="h-3 w-3" /> : <Box className="h-3 w-3" />}
                        {r.kind === "compuesto" ? "Compuesto" : "Simple"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums" data-testid={`resource-cost-${i}`}>
                      {formatEuro(r.costs?.purchase_gross ?? r.unit_cost)}
                      <div className="text-[11px] text-muted-foreground font-sans">
                        IVA {r.vat_rate ?? 21}% · {formatEuro(r.costs?.purchase_net ?? r.unit_cost)} neto
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-semibold text-[#C75B28]" data-testid={`resource-sale-price-${i}`}>
                      {formatEuro(r.costs?.sale_price ?? r.unit_cost)}
                      <div className="text-[11px] text-muted-foreground font-sans font-normal">
                        interno {formatEuro(r.costs?.internal_cost ?? r.unit_cost)}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.unit}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{r.supplier || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => openEdit(r)} data-testid={`edit-resource-${i}`} aria-label="Editar">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setToDelete(r)} data-testid={`delete-resource-${i}`} aria-label="Eliminar">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-10" data-testid="resources-no-match">
                      Ningún recurso coincide con el filtro.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ResourceFormModal open={modalOpen} onOpenChange={setModalOpen} units={units} contacts={contacts} resources={resources} activities={activities}
        onSaved={onSaved} onContactCreated={(c) => setContacts((list) => [...list, c])} resource={editing} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(v) => { if (!v) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{toDelete?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>Se quitará del catálogo de recursos. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-resource-button">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
